import { useEffect, useState } from "react"
import toast from "react-hot-toast"
import { ModuleWindowType } from "../../../components/moduleWindow/ModuleWindow"
import { translations } from "../../../configs/translations"
import { UserProfile } from "../../../models/UserProfile"
import UserService from "../../../services/UserService"
import { useStore } from "../../../store/store"
import { formatNumberInput } from "../../../utils/formatNumberInput"
import { formatTextWithParams } from "../../../utils/formatTextWithParams"
import './BattlepassBundles.css'

interface BattlepassBundlesProps {
    setModuleWindowTitle: (title: string) => void
    setModuleWindowText: (text: string) => void
    setModuleWindowOpen: (open: boolean) => void
    setModuleWindowType: (type: ModuleWindowType) => void
    isAccepted: boolean
    setInputValue: (value: string | number) => void
    inputValue: number
    setInputPrice: (value: number) => void
    inputPrice: number
    userProfile?: UserProfile
    setProfile: (profile: UserProfile | null) => void
}

export const BattlepassBundles = ({
    setModuleWindowTitle,
    setModuleWindowText,
    setModuleWindowOpen,
    setModuleWindowType,
    isAccepted,
    setInputValue,
    inputValue,
    setInputPrice,
    inputPrice,
    userProfile,
    setProfile,
}: BattlepassBundlesProps) => {
    const language = useStore(state => state.language) as keyof typeof translations
    const t = translations[language]

    const [isCustom, setIsCustom] = useState(false)
    const [type, setType] = useState<'start' | 'standard' | 'gold' | 'custom' | null>(null)

    const handleBuy = (price: number, type: 'start' | 'standard' | 'gold' | 'custom') => {

        if (type === 'start' && doesUserHaveBattlepass(userProfile)) {
            return
        }

        setIsCustom(false)
        setType(type)
        setModuleWindowType('default')
        setModuleWindowOpen(true)
        setModuleWindowTitle(t.bp_buy_window_title)
        setModuleWindowText(formatTextWithParams(t.bp_buy_window_text, [price]))
    }

    const handleCustomBuy = () => {
        setIsCustom(true)
        setInputValue(10)
        setInputPrice(getPrice('custom'))
        setModuleWindowType('with-input-battlepass')
        setModuleWindowOpen(true)
        setModuleWindowTitle(t.bp_buy_window_title)
    }

    const getDiscount = (inputValue: number) => {
        if (inputValue < 25) {
            return 0
        } else if (inputValue < 50) {
            return 5
        } else if (inputValue < 100) {
            return 15
        } else {
            return 20
        }
    }

    const doesUserHaveBattlepass = (userProfile?: UserProfile) => {
        return (userProfile?.battlepass?.experience || 0) >= 1
    }

    useEffect(() => {
        setInputValue(formatNumberInput(inputValue, 999_999_999, 0, false) as number)

        if (inputValue === 0) {
            setInputValue(10)
            setInputPrice(getPrice('custom', 10))
        }

        const discount = getDiscount(inputValue)
        if (discount == 0) {
            setModuleWindowText('')
        } else {
            setModuleWindowText(formatTextWithParams(t.bp_save_percent_for_x_levels, [discount, inputValue]))
        }

        setInputPrice(getPrice('custom', inputValue))
    }, [inputValue, userProfile])

    const audio = new Audio('/sounds/buy.mp3')
    const buyBattlepassBundle = async (type: 'start' | 'standard' | 'gold' | 'custom') => {
        await UserService.buyBattlepassBundle(type.toUpperCase())
            .then(() => {
                audio.play()
                setProfile(null)
            })
            .catch((error) => {
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    const buyBattlepassCustom = async (type: 'start' | 'standard' | 'gold' | 'custom', level: number) => {
        await UserService.buyBattlepassCustom(type.toUpperCase(), level)
            .then(() => {
                audio.play()
                setProfile(null)
            })
            .catch((error) => {
                toast.error(
                    error && error.response && error.response.data && t[error.response.data as keyof typeof t]
                        ? t[error.response.data as keyof typeof t]
                        : t.error_unknown,
                    { className: 'notification-error' }
                )
            })
    }

    useEffect(() => {
        if (isAccepted) {
            if (isCustom) {
                buyBattlepassCustom('custom', inputValue)
            } else {
                buyBattlepassBundle(type!)
            }

            setModuleWindowOpen(false)
        }
    }, [isAccepted])

    const getLvlsByType = (type: 'start' | 'standard' | 'gold' | 'custom') => {
        switch (type) {
            case 'start':
                return 1
            case 'standard':
                return 25
            case 'gold':
                return 50
            case 'custom':
                return inputValue
        }
    }

    const getPrice = (type: 'start' | 'standard' | 'gold' | 'custom', customLvls?: number) => {
        const basePrice = doesUserHaveBattlepass(userProfile) ? 0 : userProfile?.battlepass?.battlepass?.priceBase || 0
        if (customLvls === 1) return basePrice
        const lvls = customLvls || getLvlsByType(type)
        const discount = getDiscount(lvls)
        const price = basePrice + ((lvls * (userProfile?.battlepass?.battlepass?.levelPrice || 0)) * (1 - discount / 100))
        return Math.round(price)
    }

    return (
        <div className="battlepass-bundles-container">
            <div className="battlepass-bundles-header">
                <h1>{t.battlepass_title}</h1>
            </div>

            <div className="battlepass-bundles-list">

                <div className="battlepass-bundle bundle-1">

                    <div className="battlepass-bundle-content">
                        <div className="battlepass-bundle-title">
                            <h1>{t.bp_start}</h1>
                            <p>{t.level + ' 1'}</p>
                        </div>


                        <div
                            className={`battlepass-bundles-button-wrapper`}
                        >
                            <p style={{ visibility: 'hidden' }}>
                                {formatTextWithParams(t.bp_save_percent_for_x_levels, [getDiscount(1), 1])}
                            </p>
                            <div
                                onClick={() => { handleBuy(userProfile?.battlepass?.battlepass?.levelPrice || 0, 'start') }}
                                className={`battlepass-bundles-button ${doesUserHaveBattlepass(userProfile) ? 'disabled' : ''}`}
                            >
                                <div className={
                                    `battlepass-bundles-button-content`
                                }>
                                    <p>{t.buy}: {(userProfile?.battlepass?.battlepass?.priceBase || 0)} DP</p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div className="battlepass-bundle bundle-2">

                    <div className="battlepass-bundle-content">
                        <div className="battlepass-bundle-title">
                            <h1>{t.bp_standard}</h1>
                            <p>{t.level + ' 25'}</p>
                        </div>

                        <div className="battlepass-bundles-button-wrapper">
                            <p>{formatTextWithParams(t.bp_save_percent_for_x_levels, [getDiscount(25), 25])}</p>
                            <div
                                onClick={() => { handleBuy((userProfile?.battlepass?.battlepass?.levelPrice || 0) * 25, 'standard') }}
                                className="battlepass-bundles-button"
                            >
                                <div className="battlepass-bundles-button-content">
                                    <p>{t.buy}: {getPrice('standard')} DP</p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div className="battlepass-bundle bundle-3">

                    <div className="battlepass-bundle-content">
                        <div className="battlepass-bundle-title">
                            <h1>{t.bp_gold}</h1>
                            <p>{t.level + ' 50'}</p>
                        </div>

                        <div className="battlepass-bundles-button-wrapper">
                            <p>
                                {formatTextWithParams(t.bp_save_percent_for_x_levels, [getDiscount(50), 50])}
                            </p>
                            <div
                                onClick={() => { handleBuy((userProfile?.battlepass?.battlepass?.levelPrice || 0) * 50, 'gold') }}
                                className="battlepass-bundles-button"
                            >
                                <div className="battlepass-bundles-button-content">
                                    <p>{t.buy}: {getPrice('gold')} DP</p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <div className="battlepass-bundle bundle-4">

                    <div className="battlepass-bundle-content">
                        <div className="battlepass-bundle-title">
                            <h1>{t.bp_custom}</h1>
                            <p style={{ visibility: 'hidden' }}>{'1'}</p>
                        </div>

                        <div className="battlepass-bundles-button-wrapper">
                            <p style={{ visibility: 'hidden' }}>
                                {formatTextWithParams(t.bp_save_percent_for_x_levels, [getDiscount(1), 1])}
                            </p>
                            <div
                                onClick={() => { handleCustomBuy() }}
                                className="battlepass-bundles-button"
                            >
                                <div className="battlepass-bundles-button-content">
                                    <p>{t.choose_your_amount}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
        </div>
    )
}